/**
 * rolesMatrix — ÚNICA fuente de verdad de la matriz RBAC: roles, permisos,
 * mapping rol→permisos, metadata de los roleConfigs de sistema y el catálogo
 * de roles asignables.
 *
 * TRES COPIAS BYTE-IDÉNTICAS de este archivo (patrón paymentAccess/timezoneMap):
 *   - functions/utils/rolesMatrix.cjs     (CommonJS: stripeWebhook, etc.)
 *   - server/src/config/rolesMatrix.cjs   (ESM interop: roles.js re-exporta)
 *   - src/utils/rolesMatrix.js            (webpack/CRA: selectores de rol en UI)
 * El gate pre-commit (functions/scripts/check-shared-module-parity.js) FALLA
 * si las copias divergen o si difieren de server/src/config/roles.js. Editá
 * SIEMPRE las tres copias juntas y corré el test.
 *
 * Es .cjs a propósito: functions es CommonJS y server es ESM ("type":"module").
 * module.exports interopera con require (functions), import (server) y webpack
 * (CRA, que sí resuelve module.exports desde un .js).
 */

// Role definitions
const ROLES = {
  OWNER: 'owner',
  ADMIN: 'admin',
  MEMBER: 'member',
  VIEWER: 'viewer'
};

// Permission definitions
const PERMISSIONS = {
  CAN_CREATE_BOTS: 'canCreateBots',
  CAN_EDIT_BOTS: 'canEditBots',
  CAN_DELETE_BOTS: 'canDeleteBots',
  CAN_VIEW_BOTS: 'canViewBots',
  CAN_MANAGE_NUMBERS: 'canManageNumbers',
  CAN_MANAGE_INTEGRATIONS: 'canManageIntegrations',
  CAN_INVITE_MEMBERS: 'canInviteMembers',
  CAN_MANAGE_MEMBERS: 'canManageMembers',
  CAN_REMOVE_MEMBERS: 'canRemoveMembers',
  CAN_MANAGE_BILLING: 'canManageBilling',
  CAN_VIEW_ANALYTICS: 'canViewAnalytics',
  CAN_VIEW_CHATS: 'canViewChats',
  CAN_MANAGE_CHATS: 'canManageChats',
  CAN_MANAGE_ORG_SETTINGS: 'canManageOrgSettings'
};

// Role-to-permissions mapping. Los 14 flags SIEMPRE presentes en cada rol
// (denormalizados al member doc). Mantener el orden de claves estable.
const ROLE_PERMISSIONS = {
  owner: {
    canCreateBots: true,
    canEditBots: true,
    canDeleteBots: true,
    canViewBots: true,
    canManageNumbers: true,
    canManageIntegrations: true,
    canInviteMembers: true,
    canManageMembers: true,
    canRemoveMembers: true,
    canManageBilling: true,
    canViewAnalytics: true,
    canViewChats: true,
    canManageChats: true,
    canManageOrgSettings: true
  },
  admin: {
    canCreateBots: true,
    canEditBots: true,
    canDeleteBots: true,
    canViewBots: true,
    canManageNumbers: true,
    canManageIntegrations: true,
    canInviteMembers: true,
    canManageMembers: true,
    canRemoveMembers: true,
    canManageBilling: false,
    canViewAnalytics: true,
    canViewChats: true,
    canManageChats: true,
    canManageOrgSettings: false
  },
  member: {
    canCreateBots: true,
    canEditBots: true,
    canDeleteBots: false,
    canViewBots: true,
    canManageNumbers: false,
    canManageIntegrations: false,
    canInviteMembers: false,
    canManageMembers: false,
    canRemoveMembers: false,
    canManageBilling: false,
    canViewAnalytics: true,
    canViewChats: true,
    canManageChats: true,
    canManageOrgSettings: false
  },
  viewer: {
    canCreateBots: false,
    canEditBots: false,
    canDeleteBots: false,
    canViewBots: true,
    canManageNumbers: false,
    canManageIntegrations: false,
    canInviteMembers: false,
    canManageMembers: false,
    canRemoveMembers: false,
    canManageBilling: false,
    canViewAnalytics: true,
    canViewChats: true,
    canManageChats: false,
    canManageOrgSettings: false
  }
};

// Metadata de los roleConfigs de sistema (nombre/descripción visibles).
// La escribe stripeWebhook al crear la org; la lee el editor de roles.
const ROLE_METADATA = {
  owner: { name: 'Propietario', description: 'Control total de la organización' },
  admin: { name: 'Administrador', description: 'Gestión operativa sin acceso a facturación' },
  member: { name: 'Miembro', description: 'Colaborador activo sin control administrativo' },
  viewer: { name: 'Observador', description: 'Solo lectura, sin modificar nada' }
};

// Roles ASIGNABLES vía invitación / cambio de rol. owner NO es asignable
// (solo se obtiene por transfer-ownership); viewer queda RETIRADO para el
// lanzamiento (sus permisos no se enforcen en ninguna capa real — ver
// LANZAMIENTO-ESTADO.md). Backend (invite/change-role) y frontend (selectores)
// validan contra esta lista.
const ASSIGNABLE_ROLES = ['admin', 'member'];

// Get permissions for a specific role (fallback al set más restrictivo).
function getPermissions(role) {
  return ROLE_PERMISSIONS[role] || ROLE_PERMISSIONS[ROLES.VIEWER];
}

// Check if a role has a specific permission.
function hasPermission(role, permission) {
  return getPermissions(role)[permission] === true;
}

// All roles that exist in the system (incluye owner y viewer).
function getAllRoles() {
  return Object.values(ROLES);
}

// Validate a role exists at all (para deserializar / leer datos existentes).
function isValidRole(role) {
  return getAllRoles().includes(role);
}

// Validate a role can be ASSIGNED via invite/change-role (owner/viewer no).
function isAssignableRole(role) {
  return ASSIGNABLE_ROLES.includes(role);
}

module.exports = {
  ROLES,
  PERMISSIONS,
  ROLE_PERMISSIONS,
  ROLE_METADATA,
  ASSIGNABLE_ROLES,
  getPermissions,
  hasPermission,
  getAllRoles,
  isValidRole,
  isAssignableRole
};
